import { useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { downloadFile, useFile, useNewFileVersion, useRestoreFile } from '@/api/files';
import { useFolder } from '@/api/folders';
import { useWorkspaceByWorkspaceId } from '@/api/workspaces';
import { VersionHistory } from '@/components/files/VersionHistory';
import { RenameFileModal } from '@/components/files/RenameFileModal';
import { MoveFileModal } from '@/components/files/MoveFileModal';
import { DeleteConfirmModal } from '@/components/files/DeleteConfirmModal';
import { FilePreview } from '@/components/files/FilePreview';
import { ActivityTimeline } from '@/components/audit/ActivityTimeline';
import { CommentsPanel } from '@/components/comments/CommentsPanel';
import { useFileActivity } from '@/api/audit';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useIsClient } from '@/hooks/useIsClient';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import { copyToClipboard } from '@/lib/clipboard';
import { Download, FolderOpen, Link2, MoreHorizontal, Pencil, FolderInput, Trash2, RotateCcw, Upload, X } from 'lucide-react';
import type { AuditLog } from '@/types/api';

import { formatBytes } from '@/lib/formatBytes';

export function FileDetailPage() {
  const { fileId = '' } = useParams<{ fileId: string }>();
  const { data: file, isLoading, isError, error } = useFile(fileId);
  const { data: folder } = useFolder(file?.folder_id);
  const {
    data: activity,
    isLoading: activityLoading,
    isError: activityError,
    error: activityErr,
  } = useFileActivity(fileId);
  const newVersion = useNewFileVersion();
  const restore = useRestoreFile();
  const toast = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadName, setUploadName] = useState<string | null>(null);
  const [modal, setModal] = useState<'rename' | 'move' | 'delete' | null>(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [leftTab, setLeftTab] = useState<'info' | 'versions' | 'comments' | 'activity'>('info');
  const [selectedActivity, setSelectedActivity] = useState<AuditLog | null>(null);
  const isClient = useIsClient();
  usePageTitle(file?.name ?? 'File');

  const workspaceId = folder?.workspace_id ?? null;
  const { data: workspaceData } = useWorkspaceByWorkspaceId(workspaceId);

  const handleDownload = async () => {
    if (!file) return;
    try {
      await downloadFile(file.id, file.name);
    } catch (e) {
      toast.error('Download failed', apiError(e));
    }
  };

  const handleNewVersion = async (f: File) => {
    if (!file) return;
    setUploadName(f.name);
    try {
      await newVersion.mutateAsync({ fileId: file.id, file: f });
      toast.success('New version uploaded');
    } catch (e) {
      toast.error('Upload failed', apiError(e));
    } finally {
      setUploadName(null);
    }
  };

  const handleRestore = () => {
    if (!file) return;
    restore.mutate(
      { id: file.id, folder_id: file.folder_id },
      {
        onSuccess: () => toast.success('File restored'),
        onError: (e) => toast.error('Restore failed', apiError(e)),
      },
    );
  };

  return (
    <AppShell
      crumbs={[
        { label: 'Files' },
        { label: file?.name ?? 'File' },
      ]}
    >
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size={28} />
        </div>
      ) : isError || !file ? (
        <Card>
          <div className="text-sm text-red-600">{apiError(error) || 'File not found'}</div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            {/* Left column: tabbed */}
            <div className="space-y-0">
              {/* Unified card: header + tabs attached */}
              <div className="bg-white dark:bg-[#1A1A2E] border border-[#E3E3F1] dark:border-[#2A2A4A] rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.06)]">
                {/* File name header + action buttons */}
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E3E3F1] dark:border-[#2A2A4A]">
                  <div className="text-sm font-semibold text-[#12122C] dark:text-white truncate min-w-0">{file.name}</div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Desktop: individual buttons */}
                    <div className="hidden sm:contents">
                      <Button
                        variant="secondary"
                        onClick={async () => {
                          try {
                            await copyToClipboard(window.location.href);
                            toast.success('Link copied to clipboard');
                          } catch (e) {
                            toast.error('Copy failed', apiError(e));
                          }
                        }}
                        className="text-xs"
                        title="Copy link"
                      >
                        <Link2 size={14} /> Copy link
                      </Button>
                      <Button variant="primary" onClick={handleDownload} className="text-xs">
                        <Download size={14} /> Download
                      </Button>
                      {!isClient && (
                        <>
                          <input ref={fileInputRef} type="file" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleNewVersion(f); e.target.value = ''; }} />
                          <Button variant="secondary" onClick={() => fileInputRef.current?.click()} loading={newVersion.isPending} className="text-xs">
                            <Upload size={14} /> New version
                          </Button>
                          {!file.is_deleted ? (
                            <>
                              <Button variant="secondary" onClick={() => setModal('rename')} className="text-xs"><Pencil size={14} /> Rename</Button>
                              {workspaceId && <Button variant="secondary" onClick={() => setModal('move')} className="text-xs"><FolderInput size={14} /> Move</Button>}
                              <Button variant="danger" onClick={() => setModal('delete')} className="text-xs"><Trash2 size={14} /> Delete</Button>
                            </>
                          ) : (
                            <Button variant="secondary" onClick={handleRestore} loading={restore.isPending} className="text-xs"><RotateCcw size={14} /> Restore</Button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Mobile: icon-only primary actions + 3-dot menu */}
                    <div className="flex sm:hidden items-center gap-1">
                      <button onClick={handleDownload} title="Download" className="p-2 rounded-lg text-[#12122C] hover:bg-[#F2F2F8] transition-colors">
                        <Download size={18} />
                      </button>
                      <button
                        onClick={async () => { try { await copyToClipboard(window.location.href); toast.success('Link copied'); } catch { /**/ } }}
                        title="Copy link" className="p-2 rounded-lg text-[#12122C] hover:bg-[#F2F2F8] transition-colors"
                      >
                        <Link2 size={18} />
                      </button>
                      {!isClient && (
                        <div className="relative">
                          <button onClick={() => setActionsOpen((v) => !v)} className="p-2 rounded-lg text-[#12122C] hover:bg-[#F2F2F8] transition-colors">
                            <MoreHorizontal size={18} />
                          </button>
                          {actionsOpen && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setActionsOpen(false)} />
                              <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-white rounded-xl border border-[#E3E3F1] shadow-lg py-1 text-sm">
                                <input ref={fileInputRef} type="file" className="hidden"
                                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleNewVersion(f); e.target.value = ''; setActionsOpen(false); }} />
                                <button onClick={() => { fileInputRef.current?.click(); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F2F2F8] text-left">
                                  <Upload size={14} className="shrink-0 text-[#676687]" /> New version
                                </button>
                                {!file.is_deleted ? (
                                  <>
                                    <button onClick={() => { setModal('rename'); setActionsOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F2F2F8] text-left">
                                      <Pencil size={14} className="shrink-0 text-[#676687]" /> Rename
                                    </button>
                                    {workspaceId && <button onClick={() => { setModal('move'); setActionsOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F2F2F8] text-left">
                                      <FolderInput size={14} className="shrink-0 text-[#676687]" /> Move
                                    </button>}
                                    <button onClick={() => { setModal('delete'); setActionsOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F2F2F8] text-left text-red-600">
                                      <Trash2 size={14} className="shrink-0" /> Delete
                                    </button>
                                  </>
                                ) : (
                                  <button onClick={() => { handleRestore(); setActionsOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F2F2F8] text-left">
                                    <RotateCcw size={14} className="shrink-0 text-[#676687]" /> Restore
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {uploadName && (
                  <div className="px-5 py-2 text-xs text-slate-500 border-b border-[#E3E3F1] dark:border-[#2A2A4A]">
                    Uploading {uploadName}…
                  </div>
                )}

                {/* Tab bar — directly attached to the header */}
                <div className="flex gap-1 border-b border-[#E3E3F1] px-4 pt-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {(['info', 'versions', ...(!isClient ? ['comments'] : []), 'activity'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setLeftTab(t as typeof leftTab)}
                      className={cn(
                        'px-3 py-2 text-sm font-medium border-b-2 -mb-px capitalize transition-colors whitespace-nowrap',
                        leftTab === t
                          ? 'border-[#0410BD] text-[#0410BD]'
                          : 'border-transparent text-slate-500 hover:text-slate-800',
                      )}
                    >
                      {t === 'versions' ? 'Version history' : t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>

                <div className="p-5">
                  {leftTab === 'info' && (
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
                      <div>
                        <dt className="text-slate-500 dark:text-[#8888AA]">Size</dt>
                        <dd className="text-slate-800 dark:text-[#C4C4DC]">{formatBytes(file.size_bytes)}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500 dark:text-[#8888AA]">Type</dt>
                        <dd className="text-slate-800 dark:text-[#C4C4DC]">{file.mime_type || 'unknown'}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500 dark:text-[#8888AA]">Uploaded by</dt>
                        <dd className="text-slate-800 dark:text-[#C4C4DC]">{file.uploader_name ?? '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500 dark:text-[#8888AA]">Last modified</dt>
                        <dd className="text-slate-800 dark:text-[#C4C4DC]">{format(new Date(file.updated_at), 'PP p')}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500 dark:text-[#8888AA]">Uploaded</dt>
                        <dd className="text-slate-800 dark:text-[#C4C4DC]">{format(new Date(file.created_at), 'PP p')}</dd>
                      </div>
                      {folder && (
                        <div className="sm:col-span-2">
                          <dt className="text-slate-500 dark:text-[#8888AA]">Location</dt>
                          <dd className="text-slate-800 dark:text-[#C4C4DC] mt-0.5">
                            {workspaceData ? (
                              <Link
                                to={`/workspace/${workspaceData.organization_id}/folders/${folder.id}`}
                                className="inline-flex items-center gap-1.5 text-[#0410BD] hover:underline"
                              >
                                <FolderOpen size={13} className="shrink-0" />
                                <span className="truncate">{folder.path || folder.name}</span>
                              </Link>
                            ) : (
                              <span className="inline-flex items-center gap-1.5">
                                <FolderOpen size={13} className="shrink-0 text-slate-400" />
                                {folder.path || folder.name}
                              </span>
                            )}
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}
                  {leftTab === 'versions' && (
                    <VersionHistory fileId={file.id} currentVersionId={file.current_version_id} />
                  )}
                  {leftTab === 'comments' && !isClient && (
                    <CommentsPanel target={{ type: 'file', id: file.id }} />
                  )}
                  {leftTab === 'activity' && (
                    <ActivityTimeline
                      entries={activity?.items ?? []}
                      isLoading={activityLoading}
                      isError={activityError}
                      error={activityErr}
                      selectedId={selectedActivity?.id}
                      onSelect={setSelectedActivity}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Right column: activity detail or preview */}
            <div className="lg:sticky lg:top-4">
              {leftTab === 'activity' && selectedActivity ? (
                <Card>
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-800">Activity detail</span>
                    <button
                      type="button"
                      onClick={() => setSelectedActivity(null)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-xs text-slate-500 uppercase tracking-wide">What</dt>
                      <dd className="text-slate-800 mt-0.5">{selectedActivity.action.replace(/[._]/g, ' ')}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500 uppercase tracking-wide">Who</dt>
                      <dd className="text-slate-800 mt-0.5">
                        {selectedActivity.actor_name ?? 'Unknown'}
                        {selectedActivity.actor_email ? (
                          <span className="text-slate-500 ml-1 text-xs">({selectedActivity.actor_email})</span>
                        ) : null}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500 uppercase tracking-wide">When</dt>
                      <dd className="text-slate-800 mt-0.5">{format(new Date(selectedActivity.created_at), 'PPpp')}</dd>
                    </div>
                    {selectedActivity.source_ip && (
                      <div>
                        <dt className="text-xs text-slate-500 uppercase tracking-wide">IP address</dt>
                        <dd className="text-slate-800 mt-0.5 font-mono text-xs">{selectedActivity.source_ip}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-xs text-slate-500 uppercase tracking-wide">Target</dt>
                      <dd className="text-slate-800 mt-0.5 capitalize">{selectedActivity.target_type}</dd>
                    </div>
                    {selectedActivity.outcome && (
                      <div>
                        <dt className="text-xs text-slate-500 uppercase tracking-wide">Outcome</dt>
                        <dd className="text-slate-800 mt-0.5 capitalize">{selectedActivity.outcome}</dd>
                      </div>
                    )}
                    {selectedActivity.metadata && Object.keys(selectedActivity.metadata).length > 0 && (
                      <div>
                        <dt className="text-xs text-slate-500 uppercase tracking-wide mb-1">Metadata</dt>
                        <dd>
                          <div className="rounded border border-[#E3E3F1] divide-y divide-[#E3E3F1]">
                            {Object.entries(selectedActivity.metadata).map(([k, v]) => (
                              <div key={k} className="flex items-start gap-2 px-2 py-1.5">
                                <span className="text-xs text-slate-500 w-24 shrink-0 capitalize">{k.replace(/_/g, ' ')}</span>
                                <span className="text-xs text-slate-800 break-all">{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        </dd>
                      </div>
                    )}
                    {(selectedActivity.action === 'file.upload' || selectedActivity.action === 'file.version.upload') && folder && workspaceData && (
                      <div>
                        <dt className="text-xs text-slate-500 uppercase tracking-wide">Folder</dt>
                        <dd className="mt-0.5">
                          <Link
                            to={`/workspace/${workspaceData.organization_id}/folders/${folder.id}`}
                            className="inline-flex items-center gap-1.5 text-sm text-[#0410BD] hover:underline"
                          >
                            <FolderOpen size={13} className="shrink-0" />
                            {folder.path || folder.name}
                          </Link>
                        </dd>
                      </div>
                    )}
                  </dl>
                </Card>
              ) : !file.is_deleted ? (
                <FilePreview file={file} />
              ) : null}
            </div>
          </div>

          {!isClient && (
            <>
              <RenameFileModal
                open={modal === 'rename'}
                onClose={() => setModal(null)}
                file={file}
              />

              {workspaceId && (
                <MoveFileModal
                  open={modal === 'move'}
                  onClose={() => setModal(null)}
                  file={file}
                  workspaceId={workspaceId}
                />
              )}

              <DeleteConfirmModal
                open={modal === 'delete'}
                onClose={() => setModal(null)}
                file={file}
                onDeleted={() => navigate(-1)}
              />
            </>
          )}
        </>
      )}
    </AppShell>
  );
}
